import IxigoMoney from '@ixigo/icons/IxigoMoneyFilled';
import Typography from '@ixigo/iui/Typography';
import { formatPrice } from '@repo/shared/utils/priceFormatter';
import type { BaseCabItemOption, BaseFareSummary } from '@/api';
import type { DiscountFareItem } from '../index.types';

export interface FareLineItemsResult {
	/** Array of charge items to display */
	charges: BaseFareSummary[];
	/** Array of discount items to display */
	discounts: DiscountFareItem[];
	/** Line items for original booking / refund adjustments (e.g. reschedule) */
	fareAdjustmentLineItems?: DiscountFareItem[];
}

interface RescheduleAdjustmentParams {
	originalBookingAmount?: number;
	refundedAmount?: number;
	currency?: string;
}

export function getFareLineItems(
	cabPricing?: BaseCabItemOption['pricing'],
	burnOpted?: boolean,
): FareLineItemsResult {
	if (!cabPricing) {
		return {
			charges: [],
			discounts: [],
		};
	}
	const {
		basePrice,
		taxes,
		discount,
		burn,
		currency,
		originalBookingAmount,
		refundedAmount,
	} = cabPricing;

	// Build charges array
	const charges: BaseFareSummary[] = [
		{
			label: 'Cab Charges',
			value: formatPrice(basePrice, { currency }),
			amount: basePrice,
		},
		...(taxes > 0
			? [
					{
						label: 'Taxes & Fees',
						value: formatPrice(taxes, { currency }),
						amount: taxes,
					},
				]
			: []),
	];

	// Build discounts array
	const discounts: FareLineItemsResult['discounts'] = [];
	if (discount > 0) {
		discounts.push({
			label: 'Coupon Discount',
			value: `-${formatPrice(discount, { currency })}`,
			amount: discount,
			valueColor: 'text-success',
		});
	}
	if (burn > 0 && burnOpted) {
		discounts.push({
			label: (
				<Typography variant="body-md">
					Redeem ixigo Money <IxigoMoney className="size-20 text-brand" />
				</Typography>
			),
			value: `-₹${burn}`,
			amount: burn,
			valueColor: 'text-success',
		});
	}

	const fareAdjustmentLineItems = getRescheduleAdjustmentLineItems({
		originalBookingAmount,
		refundedAmount,
		currency,
	});

	return {
		charges,
		discounts,
		fareAdjustmentLineItems,
	};
}

export function getRescheduleAdjustmentLineItems({
	originalBookingAmount,
	refundedAmount,
	currency,
}: RescheduleAdjustmentParams): DiscountFareItem[] {
	const fareAdjustmentLineItems: DiscountFareItem[] = [];

	if (originalBookingAmount && originalBookingAmount > 0) {
		fareAdjustmentLineItems.push({
			label: 'Original Booking Amount',
			value: `-${formatPrice(originalBookingAmount, { currency })}`,
			amount: originalBookingAmount,
			valueColor: 'text-success',
		});
	}

	if (refundedAmount && refundedAmount > 0) {
		fareAdjustmentLineItems.push({
			label: 'Amount Refunded',
			value: `${formatPrice(refundedAmount, { currency })}`,
			amount: refundedAmount,
			valueColor: 'text-success body-sm',
			labelColor: 'text-success body-sm',
		});
	}

	return fareAdjustmentLineItems;
}
